/**
 * Central design system theme
 * Contains all design tokens for consistent styling across the app
 */

// Spacing scale
export const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  xxl: '32px',
  xxxl: '48px',
} as const

// Typography scale
export const TYPOGRAPHY = {
  caption: {
    fontSize: '13px',
    lineHeight: '1.3',
  },
  body: {
    fontSize: '16px',
    lineHeight: '1.5',
  },
  h4: {
    fontSize: '20px',
    lineHeight: '1.4',
  },
  h3: {
    fontSize: '25px',
    lineHeight: '1.3',
  },
  h2: {
    fontSize: '31px',
    lineHeight: '1.2',
  },
  h1: {
    fontSize: '39px',
    lineHeight: '1.1',
  },
} as const

// Font weight scale
export const FONT_WEIGHT = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extraBold: 800,
} as const

// Color palette
export const COLORS = {
  // Primary color and variants
  primary: '#ac6dff',
  primaryHover: '#8A72FA', // Lighter for hover states
  primaryActive: '#5B42E0', // Darker for active/pressed states
  primaryBg: 'rgba(110, 83, 249, 0.1)', // Light background tint
  primaryShadow: 'rgba(110, 83, 249, 0.35)', // For box-shadows

  // Grays
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray800: '#1f2937',
  gray900: '#111827',

  // Purple accent
  purple50: '#f5f3ff',
  purple500: '#8b5cf6',
  purple600: '#7c3aed',

  // Slate (for summarized nodes)
  slate50: '#f8fafc',
  slate400: '#94a3b8',
  slate500: '#64748b',

  // Success/Green
  success: '#22c55e',
  successLight: '#f0fdf4',
  successDark: '#16a34a',

  // Borders
  border: '#f0f0f0',
  borderDark: '#d1d5db',

  // Text - using CSS variables for dark mode support
  textPrimary: 'var(--color-text-primary)',
  textSecondary: 'var(--color-text-secondary)',
  textTertiary: 'var(--color-text-tertiary)',
  textPlaceholder: 'var(--color-text-muted)',

  // Backgrounds - using CSS variables for dark mode support
  bgPrimary: 'var(--color-bg-primary)',
  bgSecondary: 'var(--color-bg-secondary)',
  bgElevated: 'var(--color-bg-elevated)',
  bgHover: 'var(--color-bg-hover)',
  bgCard: 'var(--color-bg-card)',
  bgInput: 'var(--color-input-bg)',
  bgSelected: 'var(--color-selected-bg)',
  bgWhite: '#ffffff', // Keep for cases where pure white is needed
  bgTransparent: 'transparent',
  bgOverlay: 'rgba(255, 255, 255, 0.4)',
  bgOverlayLight: 'var(--color-overlay-light)',
  bgOverlayFull: 'rgba(255, 255, 255, 1)',

  // Border colors - using CSS variables for dark mode support
  borderLight: 'var(--color-border)',
  borderDarkVar: 'var(--color-border-dark)',
  borderCard: 'var(--color-border-card)',

  // Focus ring
  focusRing: 'var(--color-focus-ring)',
} as const

// Border radius
export const BORDER_RADIUS = {
  none: '0',
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  full: '9999px',
} as const

// Shadows
export const SHADOWS = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
} as const

// Transitions
export const TRANSITIONS = {
  fast: '0.15s ease',
  normal: '0.2s ease',
  smooth: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  all: 'all 0.2s ease',
  allSmooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
} as const

// Z-index scale
// Organized by context and stacking order
export const Z_INDEX = {
  // Base levels (within normal stacking context)
  base: 0,

  // Node-internal elements (relative to node's stacking context)
  // These are low values because they're within the node component
  nodeInternal: {
    behind: -1, // Border layers behind node
    behindDeep: -2, // Second border layer
    content: 1, // Default content
    elevated: 5, // Labels container
    focused: 10, // Focused state, collapse button
    badge: 20, // AI indicator badge
  },

  // Sidebar levels
  sidebar: 100,
  sidebarOverlay: 200,

  // React Flow node when brought to front (via useNodeZIndex)
  // This must be high enough that focused nodes appear above others
  reactFlowNodeFocused: 10000,
  reactFlowNodeThreshold: 1000, // Threshold for detecting "high" z-index nodes

  // UI overlays that must appear above React Flow nodes
  // These are for portaled elements (rendered to document.body)
  dropdown: 10100, // Dropdowns, selectors over nodes
  sticky: 10200, // Sticky headers
  fixed: 10300, // Fixed position elements
  modalBackdrop: 10400, // Modal overlay background
  modal: 10500, // Modal content
  popover: 10600, // Popovers
  tooltip: 10700, // Tooltips (highest UI element)
} as const

// Breakpoints for responsive design
const BREAKPOINTS = {
  xs: '480px',
  sm: '576px',
  md: '870px',
  lg: '992px',
  xl: '1200px',
  xxl: '1600px',
} as const

// Media query helpers
export const MEDIA = {
  mobile: `@media (max-width: ${BREAKPOINTS.md})`,
  tablet: `@media (max-width: ${BREAKPOINTS.lg})`,
  desktop: `@media (min-width: ${BREAKPOINTS.lg})`,
  smallMobile: `@media (max-width: ${BREAKPOINTS.xs})`,
} as const

// Common CSS values
export const COMMON = {
  fullWidth: '100%',
  fullHeight: '100%',
  fullViewportHeight: '100dvh',
  inheritFont: 'inherit',
} as const
